// frontend/src/pages/adolescentes/AdolescenteMovimentacoesListPage.jsx
import { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import echo from '../../services/echo';

function AdolescenteMovimentacoesListPage() {
  const { id } = useParams(); // adolescente_id
  const navigate = useNavigate();

  const [adolescente, setAdolescente] = useState(null);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helpers de formatação
  const statusBadge = (mov) => {
    const final = typeof mov.finalizada === 'boolean'
      ? mov.finalizada
      : Boolean(mov?.meio_saida?.id || mov?.decisao?.id);
    return final
      ? <span className="badge bg-success">Finalizada</span>
      : <span className="badge bg-warning text-dark">Em aberto</span>;
  };

  const decisaoLabel = (mov) => mov?.decisao?.nome || '—';
  const saidaLabel   = (mov) => mov?.meio_saida?.nome || '—';

  // Carrega cabeçalho do adolescente e lista paginada de movimentações
  useEffect(() => {
    const fetchAll = async (page) => {
      setLoading(true);
      setError(null);
      try {
        // 1) Dados do adolescente (para header)
        const aResp = await api.get(`/adolescentes/${id}`);
        setAdolescente(aResp.data?.data || null);

        // 2) Movimentações do adolescente (paginadas)
        const mResp = await api.get(`/adolescentes/${id}/movimentacoes?page=${page}`);
        setMovimentacoes(mResp.data?.data || []);
        setPaginationInfo(mResp.data?.meta || null);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar as movimentações do adolescente.');
      } finally {
        setLoading(false);
      }
    };

    fetchAll(currentPage);
  }, [id, currentPage]);

  // 🔔 WebSocket: ouve atualizações do canal público "movimentacoes"
  useEffect(() => {
    const channel = echo.channel('movimentacoes');

    const onSaved = async (evt) => {
      try {
        if (!evt?.id) return;

        // Busca a movimentação completa para sabermos o adolescente e campos ricos (decisão/saída/status)
        const resp = await api.get(`/movimentacoes/${evt.id}`);
        const mov = resp.data?.data ?? resp.data;
        if (!mov?.adolescente?.id) return;

        // Ignora eventos de outros adolescentes
        if (String(mov.adolescente.id) !== String(id)) return;

        // Atualiza/injeta a linha
        setMovimentacoes((prev) => {
          const idx = prev.findIndex((m) => m.id === mov.id);
          const row = {
            ...mov,
            situacao_atual: mov?.situacao_atual ?? mov?.situacoes?.[0] ?? null,
            created_at: mov?.created_at ?? '',
            usuario_criador: mov?.usuario_criador ?? null,
          };

          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = row;
            return next;
          }
          return [row, ...prev];
        });
      } catch (e) {
        console.error('[WS] erro ao atualizar movimentações:', e);
      }
    };

    channel.listen('.movimentacao.saved', onSaved);
    return () => channel.stopListening('.movimentacao.saved');
  }, [id]);

  const filteredMovimentacoes = useMemo(() => {
    if (!searchTerm) return movimentacoes;
    const term = searchTerm.toLowerCase();

    return movimentacoes.filter((mov) => {
      const sitAtual = mov?.situacao_atual?.nome?.toLowerCase?.() || '';
      const createdAt = (mov?.created_at || '').toLowerCase();
      const usuario = mov?.usuario_criador?.name?.toLowerCase?.() || '';
      const dec = mov?.decisao?.nome?.toLowerCase?.() || '';
      const saida = mov?.meio_saida?.nome?.toLowerCase?.() || '';
      return sitAtual.includes(term) || createdAt.includes(term) || usuario.includes(term) || dec.includes(term) || saida.includes(term);
    });
  }, [movimentacoes, searchTerm]);

  const handlePageChange = (newPage) => {
    if (paginationInfo && newPage > 0 && newPage <= paginationInfo.last_page) {
      setCurrentPage(newPage);
    }
  };

  if (loading) return <LoadingSpinner text="Carregando movimentações..." />;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <>
      {/* Header com info do Adolescente */}
      <div className="content-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h1 className="content-title">Movimentações</h1>
          <p className="content-subtitle mb-0">
            Histórico de movimentações do adolescente
          </p>
          {adolescente && (
            <div className="mt-2 small text-light">
              <span className="me-3">
                <strong>Adolescente:</strong>{' '}
                {adolescente.nome ?? adolescente.nome_adolescente ?? '—'}
              </span>
              <span className="me-3">
                <strong>Situação atual:</strong>{' '}
                {adolescente.situacao?.nome ?? adolescente.situacao_atual?.nome ?? '—'}
              </span>              
            </div>
          )}
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate('/painel/adolescentes')}
          >
            <i className="bi bi-arrow-left me-2" /> Voltar
          </button>
          <Link
            to={`/painel/adolescentes/${id}/movimentacoes/novo`}
            className="btn btn-primary"
          >
            <i className="bi bi-plus-lg me-2" />
            Nova Movimentação
          </Link>
        </div>
      </div>

      {/* Card: Filtro e Tabela */}
      <div className="card">
        <div className="card-body">
          <div className="list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="input-group" style={{ maxWidth: '520px' }}>
              <span className="input-group-text">
                <i className="bi bi-search" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por situação, status, decisão, saída, data ou usuário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Situação Atual</th>
                  <th>Decisão</th>
                  <th>Meio de Saída</th>
                  <th>Iniciada em</th>
                  <th>Usuário Responsável</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovimentacoes.length > 0 ? (
                  filteredMovimentacoes.map((mov) => (
                    <tr key={mov.id}>
                      <td data-label="ID">{mov.id}</td>
                      <td data-label="Status">{statusBadge(mov)}</td>
                      <td data-label="Situação Atual">{mov?.situacao_atual?.nome || '—'}</td>
                      <td data-label="Decisão">{decisaoLabel(mov)}</td>
                      <td data-label="Meio de Saída">{saidaLabel(mov)}</td>
                      <td data-label="Iniciada em">{mov?.created_at || '—'}</td>
                      <td data-label="Usuário Responsável">{mov?.usuario_criador?.name || '—'}</td>
                      <td className="table-actions d-flex justify-content-end gap-2" data-label="Ações">
                        <Link
                          to="/painel/dashboard"
                          className="btn btn-sm btn-outline-primary"
                          title="Ver no Dashboard"
                        >
                          <i className="bi bi-eye" />
                        </Link>
                        <button className="btn btn-sm btn-outline-warning" title="Editar" disabled>
                          <i className="bi bi-pencil" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Nenhuma movimentação encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {paginationInfo && paginationInfo.last_page > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span>
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong>
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left" /> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AdolescenteMovimentacoesListPage;
